import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { RoomEvent, ParticipantEvent, Track, ConnectionQuality, Room, Participant, RemoteTrackPublication } from 'livekit-client';
import { VideoTrack, AudioTrack, TrackRefContext } from '@livekit/components-react';
/**
 * Performance metrics tracked by the component
 */
export interface NamedMediaTrackMetrics {
    subscriptionStartTime: number | null;
    subscriptionTime: number;
    reconnectCount: number;
    errorCount: number;
}

/**
 * State types emitted by onStateChange callback
 */
export type NamedMediaTrackState =
    | 'initializing'
    | 'waiting_for_participant'
    | 'waiting_for_track'
    | 'loading'
    | 'ready'
    | 'error';

/**
 * Data passed with each state change
 */
export interface NamedMediaTrackStateData {
    identity?: string;
    trackName?: string;
    retryCount?: number;
    maxRetries?: number;
    track?: Track;
    error?: Error;
}

/**
 * Props for NamedMediaTrack component
 */
export interface NamedMediaTrackProps {
    /** Participant identity to target */
    identity: string;

    /** Exact track name to render */
    trackName: string;

    /** LiveKit Room instance (required, but can be null for graceful handling) */
    room: Room | null;

    /** Callback for error events */
    onError?: (error: Error, retryCount: number, maxRetries: number) => void;

    /** Callback when track is ready and subscribed */
    onTrackReady?: (track: Track) => void;

    /** Callback when video track is mounted and playing */
    onTrackPlaying?: (track: Track) => void;

    /** Callback for state changes */
    onStateChange?: (state: NamedMediaTrackState, data: NamedMediaTrackStateData) => void;

    /** Callback for connection quality changes */
    onConnectionQualityChange?: (quality: ConnectionQuality, participant: Participant) => void;

    /** Callback for performance metrics (optional) */
    onMetrics?: (metrics: NamedMediaTrackMetrics) => void;

    /** Maximum subscription retry attempts */
    maxRetries?: number;

    /** Initial retry delay in ms (exponential backoff) */
    retryDelay?: number;

    /** Timeout for subscription in ms */
    subscriptionTimeout?: number;

    /** Auto-recover from errors */
    autoRecoverAfterError?: boolean;

    /** Delay before auto-recovery in ms */
    errorRecoveryDelay?: number;

    /** Enable debug logging */
    debug?: boolean;

    /** Custom render function for error state */
    renderError?: (error: Error, retryCount: number, maxRetries: number) => React.ReactNode;

    /** Generic render function for all loading states (supersedes specific loading renders) */
    renderLoading?: (state: NamedMediaTrackState, data: NamedMediaTrackStateData) => React.ReactNode;

    /** Custom render function for waiting for participant */
    renderWaitingForParticipant?: (identity: string) => React.ReactNode;

    /** Custom render function for waiting for track */
    renderWaitingForTrack?: (trackName: string, identity: string) => React.ReactNode;

    /** Custom render function for loading track */
    renderLoadingTrack?: (retryCount: number, maxRetries: number) => React.ReactNode;

    /** Custom render function for poor connection indicator */
    renderPoorConnection?: () => React.ReactNode;

    /** Custom CSS class name for video element */
    videoClassName?: string;

    /** Custom inline styles for video element */
    videoStyle?: React.CSSProperties;

    /** Border radius for video element */
    videoBorderRadius?: 'none' | 'small' | 'medium' | 'large';

    /** Shadow for video element */
    videoShadow?: 'none' | 'resting' | 'above' | 'topmost';
}

/**
 * NamedMediaTrack Component
 * 
 * A hardened component for displaying LiveKit media tracks by participant identity and track name.
 * Includes comprehensive error handling, automatic recovery, and connection quality monitoring.
 * 
 * Features:
 * - Subscription timeout protection
 * - Memory leak prevention
 * - Room state validation
 * - Track replacement detection
 * - Auto-recovery from errors
 * - Performance metrics tracking
 * - Debug mode
 * - Smart video sizing (portrait/landscape detection)
 * - Configurable border radius and shadow for video element
 * - Fully customizable UI via render props
 */
export default function NamedMediaTrack({
    identity,
    trackName,
    room,
    onError,
    onTrackReady,
    onTrackPlaying,
    onStateChange,
    onConnectionQualityChange,
    onMetrics,
    maxRetries = 5,
    retryDelay = 1000,
    subscriptionTimeout = 30000,
    autoRecoverAfterError = true,
    errorRecoveryDelay = 5000,
    debug = false,
    renderError,
    renderLoading,
    renderWaitingForParticipant,
    renderWaitingForTrack,
    renderLoadingTrack,
    renderPoorConnection,
    videoClassName,
    videoStyle,
    videoBorderRadius,
    videoShadow
}: NamedMediaTrackProps): React.ReactElement | null {
    // #12 Debug logging helper
    const debugLog = useCallback((...args: unknown[]) => {
        if (debug) {
            console.log('[NamedMediaTrack DEBUG]', ...args);
        }
    }, [debug]);

    // State
    const [participants, setParticipants] = useState<Participant[]>([]);
    const [error, setError] = useState<Error | null>(null);
    const [retryCount, setRetryCount] = useState<number>(0);
    const [connectionQuality, setConnectionQuality] = useState<ConnectionQuality>(ConnectionQuality.Excellent);
    const [currentState, setCurrentState] = useState<NamedMediaTrackState>('initializing');

    // #10 Performance metrics (optional)
    const [metrics, setMetrics] = useState<NamedMediaTrackMetrics>({
        subscriptionStartTime: null,
        subscriptionTime: 0,
        reconnectCount: 0,
        errorCount: 0
    });

    // #3 Memory leak prevention - track active subscriptions
    const activeSubscriptionsRef = useRef<Set<string>>(new Set());
    const subscriptionTimeoutsRef = useRef<Set<NodeJS.Timeout>>(new Set());

    // #8 Track replacement detection
    const prevTrackRef = useRef<Track | null>(null);

    // Video orientation for smart sizing
    const [videoOrientation, setVideoOrientation] = useState<'portrait' | 'landscape' | null>(null);

    // #7 Room state validation
    const isRoomConnected = useCallback((): boolean => {
        if (!room) {
            debugLog('Room is null');
            return false;
        }
        const connected = room.state === 'connected' || room.state === 'reconnecting';
        debugLog('Room connected:', connected, 'state:', room.state);
        return connected;
    }, [room, debugLog]);

    // Handle video metadata to detect orientation for smart sizing
    const handleVideoMetadata = useCallback((event: React.SyntheticEvent<HTMLVideoElement>) => {
        const video = event.currentTarget;
        const width = video.videoWidth;
        const height = video.videoHeight;

        if (width && height) {
            const orientation = height > width ? 'portrait' : 'landscape';
            debugLog('Video dimensions:', width, 'x', height, '- Orientation:', orientation);
            setVideoOrientation(orientation);
        }
    }, [debugLog]);

    // Handle video resize to detect orientation changes (e.g., device rotation)
    const handleVideoResize = useCallback((event: React.SyntheticEvent<HTMLVideoElement>) => {
        const video = event.currentTarget;
        const width = video.videoWidth;
        const height = video.videoHeight;

        if (width && height) {
            const orientation = height > width ? 'portrait' : 'landscape';
            debugLog('Video resized - New dimensions:', width, 'x', height, '- Orientation:', orientation);
            setVideoOrientation(orientation);
        }
    }, [debugLog]);

    // Handle null room gracefully
    if (!room) {
        debugLog('Null room provided, showing error state');
        return (
            <div style={{ padding: '20px', color: '#d32f2f', textAlign: 'center' }}>
                <div>⚠️ No Room Connection</div>
                <div style={{ fontSize: '12px', marginTop: '8px' }}>Room object is not available</div>
            </div>
        );
    }

    // Helper to emit state changes
    const emitStateChange = useCallback((state: NamedMediaTrackState, data: NamedMediaTrackStateData = {}) => {
        console.log(`[NamedMediaTrack] State changed: ${state}`, data);
        setCurrentState(state);
        if (onStateChange) {
            onStateChange(state, data);
        }
    }, [onStateChange]);

    useEffect(() => {
        const updateParticipants = () => {
            try {
                if (!room || !room.localParticipant) {
                    console.warn('[NamedMediaTrack] Room or localParticipant not available');
                    setParticipants([]);
                    return;
                }

                const allParticipants = [
                    room.localParticipant,
                    ...Array.from(room.remoteParticipants.values())
                ].filter(Boolean) as Participant[];

                console.log(`[NamedMediaTrack] Updated participants: ${allParticipants.length} total`);
                setParticipants(allParticipants);
            } catch (err) {
                const error = err as Error;
                console.error('[NamedMediaTrack] Error updating participants:', error);
                setError(error);
                // State change will be emitted by useEffect
                if (onError) onError(error, 0, maxRetries);
            }
        };

        updateParticipants();

        // Listen to all relevant room events
        room.on(RoomEvent.ParticipantConnected, updateParticipants);
        room.on(RoomEvent.ParticipantDisconnected, updateParticipants);
        room.on(RoomEvent.Reconnected, updateParticipants);
        room.on(RoomEvent.Disconnected, () => {
            console.warn('[NamedMediaTrack] Room disconnected');
            setParticipants([]);
        });

        return () => {
            room.off(RoomEvent.ParticipantConnected, updateParticipants);
            room.off(RoomEvent.ParticipantDisconnected, updateParticipants);
            room.off(RoomEvent.Reconnected, updateParticipants);
            room.off(RoomEvent.Disconnected, updateParticipants);
        };
    }, [room, onError, maxRetries]);

    const participant = useMemo<Participant | null>(
        () => participants.find((p) => p.identity === identity) || null,
        [participants, identity]
    );

    const [pub, setPub] = useState<RemoteTrackPublication | null>(null);
    const [mediaTrack, setMediaTrack] = useState<Track | null>(null);

    const findPub = useCallback((): RemoteTrackPublication | null => {
        try {
            if (!participant || !participant.trackPublications) {
                console.log(`[NamedMediaTrack] No participant or trackPublications for ${identity}`);
                return null;
            }

            for (const tp of participant.trackPublications.values()) {
                if (tp && tp.trackName === trackName) {
                    console.log(`[NamedMediaTrack] Found publication: ${trackName}, subscribed: ${tp.isSubscribed}`);
                    return tp as RemoteTrackPublication;
                }
            }
            console.log(`[NamedMediaTrack] Track "${trackName}" not found for ${identity}`);
            return null;
        } catch (err) {
            const error = err as Error;
            console.error('[NamedMediaTrack] Error finding publication:', error);
            setError(error);
            // State change will be emitted by useEffect
            if (onError) onError(error, 0, maxRetries);
            return null;
        }
    }, [participant, trackName, identity, onError, maxRetries]);

    const ensureSubscribed = useCallback(async (nextPub: RemoteTrackPublication | null, attempt: number = 0): Promise<void> => {
        if (!nextPub) {
            debugLog('No publication to subscribe');
            return;
        }

        // #7 Check room connection before subscribing
        if (!isRoomConnected()) {
            console.warn('[NamedMediaTrack] Cannot subscribe - room not connected');
            return;
        }

        // #3 Generate unique subscription ID for cleanup tracking
        const subscriptionId = `${Date.now()}_${attempt}`;
        activeSubscriptionsRef.current.add(subscriptionId);
        debugLog('Starting subscription attempt', attempt + 1, 'ID:', subscriptionId);

        // #10 Track subscription start time
        if (attempt === 0 && onMetrics) {
            setMetrics(prev => ({ ...prev, subscriptionStartTime: Date.now() }));
        }

        try {
            debugLog('Ensuring subscription for', trackName, 'attempt', attempt + 1);

            // #1 Subscription timeout protection
            const timeoutId = setTimeout(() => {
                if (activeSubscriptionsRef.current.has(subscriptionId) && !nextPub.track) {
                    const err = new Error(`Subscription timeout after ${subscriptionTimeout}ms`);
                    console.error('[NamedMediaTrack]', err.message);
                    setError(err);
                    setMetrics(prev => ({ ...prev, errorCount: prev.errorCount + 1 }));
                    if (onError) onError(err, attempt, maxRetries);
                    activeSubscriptionsRef.current.delete(subscriptionId);
                }
            }, subscriptionTimeout);
            subscriptionTimeoutsRef.current.add(timeoutId);

            if (!nextPub.isSubscribed) {
                debugLog('Subscribing to', trackName);
                await nextPub.setSubscribed(true);

                // Wait a bit for track to be available
                await new Promise(resolve => setTimeout(resolve, 100));
            }

            // Check if subscription was cleaned up
            if (!activeSubscriptionsRef.current.has(subscriptionId)) {
                debugLog('Subscription cleaned up, aborting');
                clearTimeout(timeoutId);
                subscriptionTimeoutsRef.current.delete(timeoutId);
                return;
            }

            if (nextPub.track) {
                console.log(`[NamedMediaTrack] Track ready: ${trackName}`);
                setMediaTrack(nextPub.track);
                setRetryCount(0);
                setError(null);

                // #10 Calculate subscription time
                if (onMetrics && metrics.subscriptionStartTime) {
                    const subscriptionTime = Date.now() - metrics.subscriptionStartTime;
                    const newMetrics = { ...metrics, subscriptionTime, subscriptionStartTime: null };
                    setMetrics(newMetrics);
                    onMetrics(newMetrics);
                    debugLog('Subscription completed in', subscriptionTime, 'ms');
                }

                // Cleanup timeout and subscription ID
                clearTimeout(timeoutId);
                subscriptionTimeoutsRef.current.delete(timeoutId);
                activeSubscriptionsRef.current.delete(subscriptionId);

                if (onTrackReady) onTrackReady(nextPub.track);
            } else if (attempt < maxRetries) {
                // Retry with exponential backoff
                const delay = retryDelay * Math.pow(2, attempt);
                console.warn(`[NamedMediaTrack] Track not available, retrying in ${delay}ms...`);

                clearTimeout(timeoutId);
                subscriptionTimeoutsRef.current.delete(timeoutId);

                // #3 Schedule retry with cleanup tracking
                const retryTimeoutId = setTimeout(() => {
                    if (activeSubscriptionsRef.current.has(subscriptionId)) {
                        ensureSubscribed(nextPub, attempt + 1);
                    }
                }, delay);
                subscriptionTimeoutsRef.current.add(retryTimeoutId);

                setRetryCount(attempt + 1);
            } else {
                const err = new Error(`Failed to subscribe to track "${trackName}" after ${maxRetries} attempts`);
                console.error('[NamedMediaTrack]', err.message);
                setError(err);
                setMetrics(prev => ({ ...prev, errorCount: prev.errorCount + 1 }));

                clearTimeout(timeoutId);
                subscriptionTimeoutsRef.current.delete(timeoutId);
                activeSubscriptionsRef.current.delete(subscriptionId);

                if (onError) onError(err, maxRetries, maxRetries);
            }
        } catch (err) {
            const error = err as Error;
            console.error(`[NamedMediaTrack] Subscription error (attempt ${attempt + 1}):`, error);
            setMetrics(prev => ({ ...prev, errorCount: prev.errorCount + 1 }));

            if (attempt < maxRetries) {
                const delay = retryDelay * Math.pow(2, attempt);
                debugLog('Retrying in', delay, 'ms');

                // #3 Schedule retry with cleanup tracking
                const retryTimeoutId = setTimeout(() => {
                    if (activeSubscriptionsRef.current.has(subscriptionId)) {
                        ensureSubscribed(nextPub, attempt + 1);
                    }
                }, delay);
                subscriptionTimeoutsRef.current.add(retryTimeoutId);

                setRetryCount(attempt + 1);
            } else {
                setError(error);
                activeSubscriptionsRef.current.delete(subscriptionId);
                if (onError) onError(error, maxRetries, maxRetries);
            }
        }
    }, [trackName, maxRetries, retryDelay, onError, onTrackReady, subscriptionTimeout, isRoomConnected, debugLog, onMetrics, metrics]);

    useEffect(() => {
        if (!participant || !room) {
            setPub(null);
            setMediaTrack(null);
            return;
        }

        // initial locate + subscribe
        const initial = findPub();
        setPub(initial || null);
        ensureSubscribed(initial);

        // ---- handlers ----
        const onPublished = (newPub: RemoteTrackPublication) => {
            if (newPub && newPub.trackName === trackName) {
                console.log(`[NamedMediaTrack] Track published: ${trackName}`);
                setPub(newPub);
                ensureSubscribed(newPub);
            }
        };

        const onSubscribed = (publication: RemoteTrackPublication, track: Track) => {
            if (publication && publication.trackName === trackName) {
                console.log(`[NamedMediaTrack] Track subscribed: ${trackName}`);
                if (track) {
                    setMediaTrack(track);
                    setError(null);
                    if (onTrackReady) onTrackReady(track);
                }
            }
        };

        const onUnsubscribed = (publication: RemoteTrackPublication) => {
            if (publication && publication.trackName === trackName) {
                console.warn(`[NamedMediaTrack] Track unsubscribed: ${trackName}`);
                setMediaTrack(null);
            }
        };

        const onUnpublished = (publication: RemoteTrackPublication) => {
            if (publication && publication.trackName === trackName) {
                console.warn(`[NamedMediaTrack] Track unpublished: ${trackName}`);
                setMediaTrack(null);
                setPub(null);
            }
        };

        const onMuteToggle = (publication: RemoteTrackPublication) => {
            if (publication && publication.trackName === trackName) {
                console.log(`[NamedMediaTrack] Track mute toggled: ${trackName}, muted: ${publication.isMuted}`);
                // track object can change across mute/unmute
                setMediaTrack(publication.track ?? null);
            }
        };

        const onReconnected = () => {
            console.log('[NamedMediaTrack] Room reconnected, re-subscribing...');
            setError(null);
            setRetryCount(0);

            // #10 Track reconnect count
            setMetrics(prev => ({ ...prev, reconnectCount: prev.reconnectCount + 1 }));

            const again = findPub();
            setPub(again || null);
            if (again) ensureSubscribed(again);
        };

        const onConnectionQualityChanged = (quality: ConnectionQuality) => {
            console.log(`[NamedMediaTrack] Connection quality changed: ${quality}`);
            setConnectionQuality(quality);
            if (quality === ConnectionQuality.Poor) {
                console.warn('[NamedMediaTrack] Poor connection quality detected');
            }
            if (onConnectionQualityChange && participant) {
                onConnectionQualityChange(quality, participant);
            }
        };

        const onTrackStreamStateChanged = (publication: RemoteTrackPublication, streamState: Track.StreamState) => {
            if (publication && publication.trackName === trackName) {
                console.log(`[NamedMediaTrack] Track stream state changed: ${streamState}`);
            }
        };

        // ---- wire up ----
        participant.on(ParticipantEvent.TrackPublished, onPublished);
        participant.on(ParticipantEvent.TrackSubscribed, onSubscribed);
        participant.on(ParticipantEvent.TrackUnsubscribed, onUnsubscribed);
        participant.on(ParticipantEvent.TrackUnpublished, onUnpublished);
        participant.on(ParticipantEvent.TrackMuted, onMuteToggle);
        participant.on(ParticipantEvent.TrackUnmuted, onMuteToggle);
        participant.on(ParticipantEvent.ConnectionQualityChanged, onConnectionQualityChanged);
        participant.on(ParticipantEvent.TrackStreamStateChanged, onTrackStreamStateChanged);

        room.on(RoomEvent.Reconnected, onReconnected);
        room.on(RoomEvent.Reconnecting, () => {
            console.log('[NamedMediaTrack] Room reconnecting...');
            setMediaTrack(null);
        });
        room.on(RoomEvent.Disconnected, () => {
            console.warn('[NamedMediaTrack] Room disconnected');
            setMediaTrack(null);
            setPub(null);
        });

        // ---- cleanup ----
        return () => {
            debugLog('Cleaning up event listeners and active subscriptions');

            // #3 Memory leak prevention - cleanup all active subscriptions and timeouts
            activeSubscriptionsRef.current.clear();
            subscriptionTimeoutsRef.current.forEach(timeoutId => clearTimeout(timeoutId));
            subscriptionTimeoutsRef.current.clear();

            participant.off(ParticipantEvent.TrackPublished, onPublished);
            participant.off(ParticipantEvent.TrackSubscribed, onSubscribed);
            participant.off(ParticipantEvent.TrackUnsubscribed, onUnsubscribed);
            participant.off(ParticipantEvent.TrackUnpublished, onUnpublished);
            participant.off(ParticipantEvent.TrackMuted, onMuteToggle);
            participant.off(ParticipantEvent.TrackUnmuted, onMuteToggle);
            participant.off(ParticipantEvent.ConnectionQualityChanged, onConnectionQualityChanged);
            participant.off(ParticipantEvent.TrackStreamStateChanged, onTrackStreamStateChanged);

            room.off(RoomEvent.Reconnected, onReconnected);
            room.off(RoomEvent.Reconnecting, () => setMediaTrack(null));
            room.off(RoomEvent.Disconnected, () => setMediaTrack(null));
        };
    }, [participant, trackName, room, findPub, ensureSubscribed, onTrackReady, debugLog, onConnectionQualityChange]);

    // #8 Track replacement detection
    useEffect(() => {
        if (mediaTrack && prevTrackRef.current !== mediaTrack) {
            debugLog('Track replaced - new track detected');
            console.log('[NamedMediaTrack] Track replaced:', trackName);
            prevTrackRef.current = mediaTrack;

            // Reset any stall detection or other track-specific state here if needed
            // This ensures clean state when participant changes device or track is replaced
        }
    }, [mediaTrack, trackName, debugLog]);

    // #9 Auto-recovery from errors
    useEffect(() => {
        if (error && autoRecoverAfterError) {
            debugLog('Scheduling auto-recovery in', errorRecoveryDelay, 'ms');
            const timer = setTimeout(() => {
                console.log('[NamedMediaTrack] Attempting auto-recovery from error');
                setError(null);
                setRetryCount(0);

                // Try to re-find and subscribe to the track
                if (participant && room && isRoomConnected()) {
                    const again = findPub();
                    setPub(again || null);
                    if (again) ensureSubscribed(again);
                }
            }, errorRecoveryDelay);

            return () => clearTimeout(timer);
        }
    }, [error, autoRecoverAfterError, errorRecoveryDelay, participant, room, isRoomConnected, findPub, ensureSubscribed, debugLog]);

    // Error state
    if (error) {
        if (renderError) {
            return <>{renderError(error, retryCount, maxRetries)}</>;
        }
        return (
            <div style={{ padding: '20px', color: '#d32f2f', textAlign: 'center' }}>
                <div>⚠️ Track Error</div>
                <div style={{ fontSize: '12px', marginTop: '8px' }}>{error.message}</div>
                {retryCount > 0 && <div style={{ fontSize: '12px' }}>Retry attempt: {retryCount}/{maxRetries}</div>}
            </div>
        );
    }

    // Emit state changes in useEffect to avoid infinite re-renders
    useEffect(() => {
        if (error) {
            emitStateChange('error', { error, retryCount, maxRetries });
        } else if (!participant) {
            emitStateChange('waiting_for_participant', { identity });
        } else if (!pub) {
            emitStateChange('waiting_for_track', { trackName, identity });
        } else if (!mediaTrack) {
            emitStateChange('loading', { retryCount, maxRetries });
        } else {
            emitStateChange('ready', { track: mediaTrack });
        }
    }, [error, participant, pub, mediaTrack, identity, trackName, retryCount, maxRetries, emitStateChange]);

    // Waiting for participant
    if (!participant) {
        // Generic loading supersedes specific loading states
        if (renderLoading) {
            return <>{renderLoading('waiting_for_participant', { identity })}</>;
        }
        if (renderWaitingForParticipant) {
            return <>{renderWaitingForParticipant(identity)}</>;
        }
        return (
            <div style={{ padding: '20px', textAlign: 'center' }}>
                <div>⏳ Waiting for participant...</div>
                <div style={{ fontSize: '12px', marginTop: '8px' }}>{identity}</div>
            </div>
        );
    }

    // Waiting for track publication
    if (!pub) {
        // Generic loading supersedes specific loading states
        if (renderLoading) {
            return <>{renderLoading('waiting_for_track', { trackName, identity })}</>;
        }
        if (renderWaitingForTrack) {
            return <>{renderWaitingForTrack(trackName, identity)}</>;
        }
        return (
            <div style={{ padding: '20px', textAlign: 'center' }}>
                <div>⏳ Waiting for track...</div>
                <div style={{ fontSize: '12px', marginTop: '8px' }}>"{trackName}" from {identity}</div>
            </div>
        );
    }

    // Waiting for track to be ready
    if (!mediaTrack) {
        // Generic loading supersedes specific loading states
        if (renderLoading) {
            return <>{renderLoading('loading', { retryCount, maxRetries })}</>;
        }
        if (renderLoadingTrack) {
            return <>{renderLoadingTrack(retryCount, maxRetries)}</>;
        }
        return (
            <div style={{ padding: '20px', textAlign: 'center' }}>
                <div>⏳ Loading track...</div>
                {retryCount > 0 && (
                    <div style={{ fontSize: '12px', marginTop: '8px' }}>
                        Retry {retryCount}/{maxRetries}
                    </div>
                )}
            </div>
        );
    }

    // Create a TrackReference object that LiveKit components expect
    const trackRef = {
        participant: participant,
        publication: pub,
        source: pub?.source || Track.Source.Unknown
    };

    // Render video track
    if (pub.kind === 'video') {
        const poorConnectionIndicator = connectionQuality === ConnectionQuality.Poor && (
            renderPoorConnection ? renderPoorConnection() : (
                <div style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    background: 'rgba(255, 152, 0, 0.9)',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px'
                }}>
                    Poor Connection
                </div>
            )
        );

        // Compute final video style with smart sizing
        const computedVideoStyle: React.CSSProperties = {
            ...videoStyle
        };

        // Only apply smart sizing if width/height not explicitly set
        const hasExplicitWidth = videoStyle?.width !== undefined;
        const hasExplicitHeight = videoStyle?.height !== undefined;

        if (!hasExplicitWidth && !hasExplicitHeight && videoOrientation) {
            // Apply object-fit contain to ensure video stays within bounds while maintaining aspect ratio
            computedVideoStyle.objectFit = 'contain';
            computedVideoStyle.maxWidth = '100%';
            computedVideoStyle.maxHeight = '100%';

            if (videoOrientation === 'portrait') {
                // Portrait: constrain height, let width be auto
                computedVideoStyle.height = '100%';
                computedVideoStyle.width = 'auto';
                debugLog('Applying portrait sizing: height 100%, width auto, object-fit contain');
            } else {
                // Landscape: constrain width, let height be auto
                computedVideoStyle.width = '100%';
                computedVideoStyle.height = 'auto';
                debugLog('Applying landscape sizing: width 100%, height auto, object-fit contain');
            }
        }

        // Apply border radius and shadow if specified
        if (videoBorderRadius) {
            const borderRadiusMap = {
                none: '0',
                small: '0.25rem',
                medium: '0.5rem',
                large: '1rem'
            };
            computedVideoStyle.borderRadius = borderRadiusMap[videoBorderRadius];
        }

        if (videoShadow) {
            const shadowMap = {
                none: 'none',
                resting: '0 1px 3px rgba(0, 0, 0, 0.12)',
                above: '0 4px 8px rgba(0, 0, 0, 0.16)',
                topmost: '0 8px 16px rgba(0, 0, 0, 0.24)'
            };
            computedVideoStyle.boxShadow = shadowMap[videoShadow];
        }

        return (
            <>
                <TrackRefContext.Provider value={trackRef}>
                    <VideoTrack
                        className={videoClassName}
                        style={computedVideoStyle}
                        onLoadedMetadata={handleVideoMetadata}
                        onResize={handleVideoResize}
                        onPlaying={() => {
                            debugLog('Video is now playing');
                            if (onTrackPlaying && pub?.track) {
                                onTrackPlaying(pub.track);
                            }
                        }}
                    />
                </TrackRefContext.Provider>
                {poorConnectionIndicator}
            </>
        );
    }

    // Render audio track
    if (pub.kind === 'audio') {
        return (
            <TrackRefContext.Provider value={trackRef}>
                <AudioTrack />
            </TrackRefContext.Provider>
        );
    }

    // Unknown track type - fire error and return null
    const unknownTrackError = new Error(`Unknown track type: ${pub.kind}`);
    console.error('[NamedMediaTrack]', unknownTrackError.message);
    if (onError) {
        onError(unknownTrackError, 0, maxRetries);
    }
    return null;
}
